import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Input,
  Row,
  Switch,
  Typography,
} from 'antd';
import { useEffect, useState, type FC } from 'react';

import type { ModelSocialIntegrationEmailParsingRuleWithTags } from '@app/api/SocialIntegrationEmailParsingRule';
import { TagSelect } from '@app/components';
import type { TagOption } from '@app/components/RecordModal/components/RecordDetails/types';

import type { onChangeRuleType } from '@pages/IntegrationParsing/types';

const PF = 'integration-parsing__rule';
const { Text } = Typography;

interface Props extends ModelSocialIntegrationEmailParsingRuleWithTags {
  onChange: onChangeRuleType;
  index: number;
  tagOptions: TagOption[];
  onRemove: (index: number) => void;
  onlyOne: boolean;
  refetchTags: () => void;
}

export const Rule: FC<Props> = ({
  index,
  onChange,
  tagOptions,
  SourceEmailsList = [],
  CheckSpam,
  ExtractFullName,
  ExtractPhone,
  ExtractGender,
  ExtractLanguage,
  ExtractEmail,
  AssignTags,
  TagExtractionKeywordList,
  FixedTags,
  IsOn,
  onRemove,
  onlyOne,
  refetchTags,
}) => {
  const [rawValue, setRawValue] = useState(
    TagExtractionKeywordList?.join(', ') || ''
  );

  useEffect(() => {
    setRawValue(TagExtractionKeywordList?.join(', ') || '');
  }, [TagExtractionKeywordList]);

  return (
    <Card
      title={
        <Flex className="full-width" justify="space-between" align="center">
          <Flex gap={8} align="center">
            <Switch
              value={IsOn}
              onChange={(checked) => onChange(index, 'IsOn', checked)}
            />
            <div className={`${PF}__title`}>
              Правила парсинга входящих писем #{index + 1}
            </div>
          </Flex>

          {!onlyOne && (
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => onRemove(index)}
              size="small"
            />
          )}
        </Flex>
      }
      variant="outlined"
      type="inner"
    >
      <Flex vertical gap={16} className="full-width">
        <div className={`${PF}__section`}>
          <div className={`${PF}__section-title`}>Электронная почта</div>
          <div className={`${PF}__section-subtitle`}>
            Перечислите email-адреса с которых принимаем письма для парсинга.
            При отсутствии обрабатываются все входящие.
          </div>
          <Input
            value={SourceEmailsList?.join(', ')}
            onChange={({ target: { value } }) =>
              onChange(index, 'SourceEmailsList', value.split(', '))
            }
          />
          <Checkbox
            checked={CheckSpam}
            onChange={({ target: { checked } }) =>
              onChange(index, 'CheckSpam', checked)
            }
          >
            Проверка письма на спам
          </Checkbox>
        </div>

        <div className={`${PF}__section`}>
          <div className={`${PF}__section-title`}>Извлечь данные:</div>
          <Row>
            <Col span={12}>
              <Flex vertical gap={6} className="full-width">
                <Checkbox
                  checked={ExtractFullName}
                  onChange={({ target: { checked } }) =>
                    onChange(index, 'ExtractFullName', checked)
                  }
                >
                  Фамилия Имя Отчество клиента
                </Checkbox>
                <Checkbox
                  checked={ExtractPhone}
                  onChange={({ target: { checked } }) =>
                    onChange(index, 'ExtractPhone', checked)
                  }
                >
                  Телефон клиента
                </Checkbox>
                <Checkbox
                  checked={ExtractEmail}
                  onChange={({ target: { checked } }) =>
                    onChange(index, 'ExtractEmail', checked)
                  }
                >
                  Email клиента
                </Checkbox>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex vertical gap={6} className="full-width">
                <Checkbox
                  checked={ExtractGender}
                  onChange={({ target: { checked } }) =>
                    onChange(index, 'ExtractGender', checked)
                  }
                >
                  Пол клиента
                </Checkbox>
                <Checkbox
                  checked={ExtractLanguage}
                  onChange={({ target: { checked } }) =>
                    onChange(index, 'ExtractLanguage', checked)
                  }
                >
                  Язык общения
                </Checkbox>
              </Flex>
            </Col>
          </Row>
        </div>

        <div className={`${PF}__section`}>
          <div className={`${PF}__section-title`}>
            Извлечь после текста и присвоить теги клиенту:
          </div>
          <Text>
            После написанного текста система будет искать теги перечисленные
            через запятую и до точки.
          </Text>
          <Input
            value={rawValue}
            onChange={({ target: { value } }) => {
              setRawValue(value);
            }}
            onBlur={() => {
              const parsed = rawValue
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
              onChange(index, 'TagExtractionKeywordList', parsed);
            }}
          />
        </div>

        <div className={`${PF}__section`}>
          <div className={`${PF}__section-title`}>
            Найти и присвоить{' '}
            <b className={`${PF}__section-title--bold`}>теги клиенту:</b>
          </div>
          <TagSelect
            disabled={false}
            className={`${PF}_fields_tags_select`}
            placeholder="Выберите теги"
            size="large"
            showSearch
            options={tagOptions}
            value={FixedTags?.map(({ Id }) => Id) ?? []}
            onChange={(value) => {
              onChange(
                index,
                'FixedTags',
                value.map((id) => ({ Id: id, Name: null }))
              );
            }}
            addable
            afterAddTag={refetchTags}
          />
        </div>

        <div className={`${PF}__section`}>
          <div className={`${PF}__section-title`}>
            Присвоить{' '}
            <b className={`${PF}__section-title--bold`}>теги клиенту:</b>
          </div>
          <TagSelect
            disabled={false}
            className={`${PF}_fields_tags_select`}
            placeholder="Выберите теги"
            size="large"
            showSearch
            options={tagOptions}
            value={AssignTags?.map(({ Id }) => Id) ?? []}
            onChange={(value) => {
              onChange(
                index,
                'AssignTags',
                value.map((id) => ({ Id: id, Name: null }))
              );
            }}
            addable
            afterAddTag={refetchTags}
          />
        </div>
      </Flex>
    </Card>
  );
};
